/**
 * Scrolls to the top of the page with smooth animation
 */
export const scrollToTop = (): void => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  /**
   * Scrolls to a specific element on the page by its ID
   * @param elementId - The ID of the element to scroll to
   * @param offset - Optional offset from the top of the element (default: 0)
   */
  export const scrollToElement = (elementId: string, offset: number = 0): void => {
    const element = document.getElementById(elementId);
    
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  
  /**
   * Determines if an element is in the viewport
   * @param element - The element to check
   * @param offset - Optional offset from the viewport edge (default: 0)
   * @returns Boolean indicating if the element is in viewport
   */
  export const isInViewport = (element: HTMLElement, offset: number = 0): boolean => {
    if (!element) return false;
    
    const rect = element.getBoundingClientRect();
    
    return (
      rect.top >= 0 - offset &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + offset &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };
  
  /**
   * Sets up intersection observer to detect when elements enter viewport
   * @param selector - CSS selector for elements to observe
   * @param callback - Callback function when element enters viewport
   * @param options - IntersectionObserver options
   * @returns Cleanup function to disconnect observer
   */
  export const setupIntersectionObserver = (
    selector: string,
    callback: (entry: IntersectionObserverEntry) => void,
    options: IntersectionObserverInit = { threshold: 0.1 }
  ): () => void => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback(entry);
        }
      });
    }, options);
    
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => observer.observe(element));
    
    // Return cleanup function
    return () => observer.disconnect();
  };